import { useDashboardRequest } from './useDashboardRequest';
import { isFinance } from './financeAccess';
import { readViewerDepartmentId } from './viewerDepartment';

// What the person looking may set (the `configAccess` part of GET warning-dashboard/my-view):
//   level  'finance'  the Finance department - every threshold
//          'grants'   Finance gave them access to some threshold setups - exactly the ones in `keys`, for every department
//          'none'     nothing
//   keys   the threshold screens they may use, by the screen's address segment ('lcr-config', 'target-slab-config', 'refunds-config' ...).
//          A setup shared by several metrics (the target slabs behind TAR / ARPU / Number of Admissions / LCRR, the department thresholds behind
//          ACL / CCR / FDR / SAR / ATP) is ONE key, so access to one of those metrics is access to all of them.
// The Configure buttons / gears / pages follow this; the server enforces the same rule on every config endpoint (requireConfigAccess), so this
// is the friendly part. If the answer is missing (it has not arrived yet, an older server, a failed lookup) the old Finance-only rule applies -
// nothing ever opens up on a guess. Only Finance gives or takes away access (the Manage access window).
export const useConfigAccess = () => {
  const { data, error } = useDashboardRequest('warning-dashboard/my-view');
  const answer = data?.configAccess;
  const known = !!answer && ['finance', 'grants', 'none'].includes(answer.level);
  const level = known ? answer.level : (isFinance() ? 'finance' : 'none');
  const keys = known && Array.isArray(answer.keys) ? answer.keys : [];

  return {
    level,
    keys,
    // false only while a non-Finance person's answer is still on its way (Finance is known at once from the login data). ANY answer settles it -
    // even one without a configAccess part (an older server, a failed lookup): then the old Finance-only rule applies and the page decides.
    ready: !!data || !!error || isFinance(),
    // may this person open / use the threshold screen at <configPath>?  ('lcr-config', 'sfr-config', ...)
    canConfigure: (configPath) => level === 'finance' || (level === 'grants' && keys.includes(configPath)),
    // The department a form starts on: the person's OWN department, when it is one of the choices ('' otherwise). They can change it freely - there is
    // no department limit.
    defaultDepartment: (list) => {
      const own = readViewerDepartmentId();
      return list.some((d) => Number(d.id) === own) ? own : '';
    },
  };
};

export default useConfigAccess;
